import {
  Flex,
  Avatar,
  Box,
  Text,
  Button,
  useDisclosure,
  Icon,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOutreachById } from '@/store/outreach/actions';
import { selectOutreach } from '@/store/outreach/selectors';
import { NotificationCard, useNotificationCard } from '../components/Card';
import { NotificationTime } from '../components/Time';
import { SanitizedBox } from '@/ui/SanitizedDom';
import { useState } from 'react';
import { UpdateStage, RejectStage } from '../components/UpdateStage';
import OutreachReplied from '@/assets/icons/notification/OutreachReplied';
import { useMixpanel } from '@/hooks/use-mixpanel';
import { decode } from 'js-base64';
import { FaCheckCircle } from 'react-icons/fa';
import { NotificationLink } from '../components/NotificationLink';
import { useKFlags } from '@/hooks/use-k-flags';

const Email = ({ content }) => {
  const [showEmail, setShowEmail] = useState(false);

  return (
    <Box mt="8px">
      <Box borderLeft="4px solid #E5E5E5" pl="12px">
        <Box maxH={!showEmail ? '40px' : 'auto'} overflow="hidden">
          <SanitizedBox html={content} fontSize="12px" />
        </Box>
        <Box
          color="#0043CE"
          fontSize="12px"
          mt="12px"
          cursor="pointer"
          onClick={() => setShowEmail(!showEmail)}
        >
          {showEmail ? 'Show less' : 'Show more'}
        </Box>
      </Box>
    </Box>
  );
};

export const RepliedWrapper = ({
  setMarkAll = () => {},
  notification = {},
  onClose = () => {},
}) => {
  const { track } = useMixpanel();
  const { ats_enabled } = useKFlags();
  const {
    isOpen: isInterestedOpen,
    onOpen: onInterestedOpen,
    onClose: onInterestedClose,
  } = useDisclosure();
  const {
    isOpen: isNotInterestedOpen,
    onOpen: onNotInterestedOpen,
    onClose: onNotInterestedClose,
  } = useDisclosure();

  const { custom_attributes } = notification;
  const data = JSON.parse(decode(custom_attributes));
  const { markAsRead } = useNotificationCard();

  const [outreachCandidateStage, setOutreachCandidateStage] = useState(
    data?.outreach_candidate_stage || ''
  );
  const dispatch = useDispatch();
  const outreachDetails = useSelector(selectOutreach);

  useEffect(() => {
    if (ats_enabled) {
      dispatch(fetchOutreachById({ outreachId: data?.outreach_id }));
    }
  }, [data?.outreach_id, dispatch, ats_enabled]);

  return (
    <>
      <Flex>
        <Box>
          <Box height="40px" pos="relative">
            <Avatar
              width="36px"
              height="36px"
              borderRadius="8px"
              className="avatar-md"
              name={data?.contact_name}
              src={data?.contact_url}
            />
            <Box pos="absolute" bottom="-24px" left="9px">
              <Icon as={OutreachReplied} width="40px" height="40px" />
            </Box>
          </Box>
        </Box>
        <Box ml="12px">
          <Text style={{ fontSize: '12px' }}>
            <NotificationLink
              to={`/contacts/${data?.contact_id}`}
              label={data.contact_name}
              onClose={onClose}
              notification={notification}
              data-test-id="contact-link"
            />
            have responded to the Flow mail
            <NotificationLink
              to={`/flows/${data?.outreach_id}`}
              label={data?.outreach_name}
              onClose={onClose}
              notification={notification}
              data-test-id="flow-link"
            />
          </Text>
          <Email content={data.content.body} />
          <NotificationTime notification={notification} />

          {outreachCandidateStage === 'candidate_interested' ||
          outreachCandidateStage === 'candidate_not_interested' ? (
            <Box mt="12px" mb="12px">
              <Flex alignItems="center">
                <FaCheckCircle
                  style={{ color: 'green', backgroundColor: 'white' }}
                  size="12px"
                />
                <Text style={{ fontWeight: 'bold', fontSize: '12px' }} ml={1}>
                  Candidate marked as{' '}
                  {outreachCandidateStage === 'candidate_interested'
                    ? "'Interested'"
                    : "'Not interested'"}
                  <br />
                </Text>
              </Flex>
            </Box>
          ) : (
            <>
              <Box mt="12px">
                <Button
                  onClick={() => {
                    setMarkAll(true);
                    markAsRead();
                    onInterestedOpen();
                    track({
                      module: 'in-app-notification',
                      data: {
                        'user-event': 'click',
                        'user-action': 'mark-interested',
                      },
                    });
                  }}
                  fontSize={'12px'}
                  data-test-id="interested-button"
                >
                  Interested
                </Button>
                <Button
                  ml="12px"
                  variant="secondary"
                  onClick={() => {
                    setMarkAll(true);
                    markAsRead();
                    onNotInterestedOpen();
                    track({
                      module: 'in-app-notification',
                      data: {
                        'user-event': 'click',
                        'user-action': 'mark-not-interested',
                      },
                    });
                  }}
                  fontSize={'12px'}
                  data-test-id="not-interested-button"
                >
                  Not Interested
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Flex>
      {isInterestedOpen && (
        <UpdateStage
          onSaveSuccess={() => {
            setOutreachCandidateStage('candidate_interested');
          }}
          isOpen={isInterestedOpen}
          onClose={() => {
            setMarkAll(false);
            onInterestedClose();
          }}
          candidate={{ id: data?.contact_id, full_name: data?.contact_name }}
          outreachId={data?.outreach_id}
          outreach={outreachDetails}
        />
      )}
      {isNotInterestedOpen && (
        <RejectStage
          onSaveSuccess={() => {
            setOutreachCandidateStage('candidate_not_interested');
          }}
          isOpen={isNotInterestedOpen}
          onClose={() => {
            setMarkAll(false);
            onNotInterestedClose();
          }}
          candidate={{ id: data?.contact_id, full_name: data?.contact_name }}
          outreachId={data?.outreach_id}
          outreach={outreachDetails}
        />
      )}
    </>
  );
};

export const Replied = ({
  setMarkAll = () => {},
  notification = {},
  onClose = () => {},
}) => {
  return (
    <NotificationCard notification={notification}>
      <RepliedWrapper
        notification={notification}
        setMarkAll={setMarkAll}
        onClose={onClose}
      />
    </NotificationCard>
  );
};
